import React from 'react';
import { Link } from 'react-router-dom';
import { Timer, Trophy, User, Radio } from 'lucide-react';
import {
  LapDeltaBadge, TrackPositionBar, FlagCautionIndicator, IncidentPill,
  isShiftLight, isCaution, RigStatusBadge, isSessionInitialising, GridInitialisingPlaceholder,
} from '@/components/telemetry/TelemetryVisuals';
import RevStrip from '@/components/telemetry/RevStrip';
import FpsBadge from '@/components/telemetry/FpsBadge';
import { prettifyAssettoName, prettifyDriverName, isAcFamily } from '@/lib/telemetryPrettify';
import { useSpeedUnit, formatSpeed, speedUnitLabel } from '@/hooks/useSpeedUnit';

const SIM_BADGE = {
  iracing: { label: 'iRACING', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  assetto_corsa: { label: 'ASSETTO', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  assetto_corsa_competizione: { label: 'ACC', cls: 'bg-sky-500/15 text-sky-400 border-sky-500/30' },
  none: { label: 'IDLE', cls: 'bg-white/5 text-white/40 border-white/10' },
};

function fmtGear(g) {
  if (g === null || g === undefined) return '–';
  if (g < 0) return 'R';
  if (g === 0) return 'N';
  return String(g);
}

/**
 * Compact live rig card.
 *
 * With `onOpen` it acts on the screen it is on — the venue board opens the rig
 * detail in place — and without it, on the public page, it links through to the
 * rig's own page.
 */
export default function RigTelemetryCard({ rig, onOpen }) {
  const last = rig.last_update ? new Date(rig.last_update).getTime() : 0;
  const live = last && (Date.now() - last < 6000);
  const standby = !last || (Date.now() - last >= 60000);

  const simBadge = SIM_BADGE[rig.sim] || SIM_BADGE.none;
  const rpmPct = rig.max_rpm && rig.rpm ? Math.min(100, (rig.rpm / rig.max_rpm) * 100) : null;
  const shiftFlash = isShiftLight(rig);
  const showCaution = isCaution(rig.session_flags);
  const showPos = live && rig.position != null && rig.position > 0;
  const speedUnit = useSpeedUnit();
  // Reporting, but the sim hasn't published its session metadata yet — hold a
  // placeholder rather than an empty cockpit.
  const initialising = isSessionInitialising(rig);

  // Derive a short rig number from rig_key (e.g. "rig_03" -> "RIG 03")
  const rigNum = rig.rig_key ? rig.rig_key.replace(/[^0-9]/g, '') : '';
  const rigLabel = rigNum ? `RIG ${rigNum.padStart(2, '0')}` : rig.rig_key.toUpperCase();

  // Display-side prettifier safety net — cleans any raw IDs that were stored
  // before the ingest-side prettifier was added.
  const displayDriver = prettifyDriverName(rig.driver_name, rig.rig_key) || 'Guest Driver';
  const displayCar = isAcFamily(rig.sim) ? prettifyAssettoName(rig.car) : rig.car;
  const displayTrack = isAcFamily(rig.sim) ? prettifyAssettoName(rig.track) : rig.track;

  const cardClass = `group relative block w-full text-left border overflow-hidden transition-all duration-300 ${
    standby
      ? 'opacity-55 border-white/5 bg-[#111115]'
      : shiftFlash
        ? 'animate-card-border-flash border-[#dd1f26] bg-gradient-to-b from-[#1a1213] to-[#121214]'
        : live
          ? 'border-[#dd1f26] shadow-[0_0_15px_rgba(221,31,38,0.35)] animate-pulse bg-gradient-to-b from-[#1a1213] to-[#121214]'
          : 'border-white/10 bg-[#111115]'
  }`;

  const body = (
    <>
      {/* live accent bar (suppressed while shift-flashing) */}
      {live && !shiftFlash && <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary animate-pulse" />}

      {/* header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-2.5">
          <Radio className={`w-4 h-4 ${live ? 'text-primary' : 'text-white/30'}`} />
          <div className="flex items-center gap-1.5">
            <p className="text-white font-bold text-sm leading-tight tracking-wide uppercase">
              {rig.rig_name || rig.rig_key}
            </p>
            <span className="text-white/20 text-xs">•</span>
            <p className="text-white/40 text-[10px] font-mono tracking-widest uppercase">
              {rigLabel}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {showPos && (
            <span className="inline-flex items-center font-mono font-bold text-[10px] px-2 py-0.5 border border-primary/30 bg-primary/10 text-primary">
              P{rig.position}
            </span>
          )}
          {showCaution && <FlagCautionIndicator sessionFlags={rig.session_flags} size="sm" />}
          {rig.sim && rig.sim !== 'none' && (
            <span className={`text-[9px] font-bold tracking-widest uppercase px-2 py-1 border ${simBadge.cls}`}>
              {simBadge.label}
            </span>
          )}
          <RigStatusBadge rig={rig} size="sm" />
        </div>
      </div>

      {/* body — conditional on standby vs live */}
      {standby ? (
        /* STANDBY PLACEHOLDER */
        <div className="px-4 py-10 flex flex-col items-center justify-center gap-2">
          <Radio className="w-5 h-5 text-white/15" />
          <p className="text-white/30 text-xs font-bold tracking-widest uppercase">Rig on Standby</p>
          <p className="text-white/20 text-[10px] font-medium">Awaiting Driver</p>
        </div>
      ) : initialising ? (
        /* COLD START — first packets in, session metadata not yet published */
        <GridInitialisingPlaceholder />
      ) : (
        /* LIVE COMPACT LAYOUT */
        <div className="p-4 space-y-3">
          {/* horizontal cockpit row: speed + gear + rpm */}
          <div className="flex items-end justify-between gap-3">
            {/* speed */}
            <div>
              <p className="text-white/30 text-[9px] font-bold tracking-widest uppercase mb-0.5">Speed</p>
              <div className="flex items-baseline gap-1">
                <span className="text-white font-black text-3xl tabular-nums leading-none font-mono">
                  {formatSpeed(rig.speed_kph, speedUnit)}
                </span>
                <span className="text-white/40 text-[10px] font-bold">{speedUnitLabel(speedUnit)}</span>
              </div>
            </div>
            {/* gear */}
            <div className="text-center">
              <p className="text-white/30 text-[9px] font-bold tracking-widest uppercase mb-0.5">Gear</p>
              <span className={`font-black text-3xl font-mono leading-none ${
                rig.gear && rig.gear > 0 ? 'text-primary' : 'text-white/60'
              }`}>
                {fmtGear(rig.gear)}
              </span>
            </div>
            {/* compact rpm strip */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-white/30 text-[9px] font-bold tracking-widest uppercase">RPM</span>
                <div className="flex items-center gap-2">
                  <FpsBadge fps={rig.fps} />
                  <span className="text-white/50 text-[9px] font-mono tabular-nums">
                    {rig.rpm != null ? Math.round(rig.rpm).toLocaleString() : '–'}
                  </span>
                </div>
              </div>
              <RevStrip rpmPct={rpmPct} shiftFlash={shiftFlash} size="sm" />
            </div>
          </div>

          {/* track position bar (only when live) */}
          <TrackPositionBar pct={rig.lap_dist_pct} accent="#dd1f26" size="sm" />

          {/* compact single-row timing bar */}
          <div className="flex items-center gap-3 bg-black/30 border border-white/5 px-3 py-2">
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <Timer className="w-3 h-3 text-white/40 shrink-0" />
              <span className="text-white/30 text-[9px] font-bold tracking-widest uppercase shrink-0">Last</span>
              <span className="text-white font-mono font-bold text-xs tabular-nums truncate">{rig.lap_time || '–'}</span>
              <LapDeltaBadge delta={rig.lap_delta} size="sm" />
            </div>
            <div className="w-px h-4 bg-white/10" />
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <Trophy className="w-3 h-3 text-primary/70 shrink-0" />
              <span className="text-white/30 text-[9px] font-bold tracking-widest uppercase shrink-0">Best</span>
              <span className="text-primary font-mono font-bold text-xs tabular-nums truncate">{rig.best_lap_time || '–'}</span>
            </div>
          </div>

          {/* meta row — compact */}
          <div className="flex items-center justify-between pt-2 border-t border-white/5 text-[11px]">
            <span className="flex items-center gap-1.5 text-white/40 truncate">
              <User className="w-3 h-3 shrink-0" />
              <span className="truncate">{displayDriver || '–'}</span>
            </span>
            <div className="flex items-center gap-2 shrink-0">
              {rig.lap != null && rig.lap > 0 && (
                <span className="text-white/30">L<span className="text-white/50 font-mono">{rig.lap}</span></span>
              )}
              {rig.session_type && (
                <span className="text-white/40 font-bold tracking-wider uppercase text-[9px]">{rig.session_type}</span>
              )}
              {rig.incidents != null && <IncidentPill incidents={rig.incidents} size="sm" />}
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (onOpen) {
    return (
      <button
        type="button"
        onClick={() => onOpen(rig.rig_key)}
        className={cardClass}
        aria-label={`Open ${rig.rig_name || rig.rig_key} live detail`}
      >
        {body}
      </button>
    );
  }

  return (
    <Link to={`/live/${rig.rig_key}`} className={cardClass}>
      {body}
    </Link>
  );
}